import {
  DimeCard,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { ListErrorState } from '../../../../components/ListErrorState.js';
import { assertUuidRouteParams } from '../../../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../../../modules/iam/permissions-context.js';
import { canCreateLeafLinkCustomer } from '../../../../modules/leaflink-companies/can-create-leaflink-customer.js';
import { LeafLinkCustomerImportWizard } from '../../../../modules/leaflink-companies/leaflink-customer-import-wizard.js';
import { LeafLinkWebUrlCompanyContext } from '../../../../modules/leaflink-companies/leaflink-web-url-company-context.js';
import { toUsableLeafLinkCompanyBrands } from '../../../../modules/leaflink-companies/to-usable-leaflink-company-brands.js';
import { useLeafLinkCompanyQuery } from '../../../../modules/leaflink-companies/use-leaflink-company-query.js';

export const Route = createFileRoute('/integrations/leaflink/$leafLinkIntegrationId/customers')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: LeafLinkCustomersPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkCustomersPage() {
  const { leafLinkIntegrationId } = Route.useParams();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('leafLinkIntegration:edit');
  const canCreateCustomer = canCreateLeafLinkCustomer(permissions);

  const {
    data: companyData,
    error: companyError,
    refetch: refetchCompany,
  } = useLeafLinkCompanyQuery(leafLinkIntegrationId);
  const company = companyData?.leafLinkCompany ?? null;

  const filterableBrands = useMemo(
    () => toUsableLeafLinkCompanyBrands(company?.leafLinkCompanyBrand ?? []),
    [company],
  );

  return (
    <LeafLinkWebUrlCompanyContext.Provider value={company}>
      <DimeCard borderless className="gap-0 py-0">
        <DimeCardHeader className="px-0 mb-4">
          <DimeCardTitle className="text-base">
            {company ? `${company.name} — Import LeafLink Customers` : 'Import LeafLink Customers'}
          </DimeCardTitle>
          <DimeCardDescription className="text-sm">
            Pull a brand's LeafLink customers for a selected state into Nudool and import new ones.
            LeafLink's own brand field isn't reliably populated for customers, so results are scoped
            to the company and state, not strictly to this brand — review before importing.
          </DimeCardDescription>
        </DimeCardHeader>
        {companyError ? (
          <ListErrorState
            title="Could not load this LeafLink company"
            message={companyError.message}
            onRetry={() => void refetchCompany()}
          />
        ) : (
          <LeafLinkCustomerImportWizard
            leafLinkIntegrationId={leafLinkIntegrationId}
            filterableBrands={filterableBrands}
            canEdit={canEdit}
            canCreateCustomer={canCreateCustomer}
          />
        )}
      </DimeCard>
    </LeafLinkWebUrlCompanyContext.Provider>
  );
}
